import React, { FC } from 'react';

import { gql } from '@apollo/client';

import {
  Permission,
  useMicrosoftCustomEmployeeIntegrationExpirationQuery,
  useMicrosoftServiceUserIntegrationExpirationQuery,
} from 'src/generated/mloop-graphql';

import { useOrgPrefStringArray } from 'src/hooks/api/org';
import { useHasUserPermission } from 'src/hooks/api/user/get';

import { OrgPrefName } from 'src/utils/api/org/constants';

import MicrosoftCustomExpirationBanner, {
  MicrosoftCustomExpirationBannerFragments,
} from './MicrosoftCustomExpirationBanner';
import MicrosoftServiceUserExpirationBanner, {
  MicrosoftServiceUserExpirationBannerFragments,
} from './MicrosoftServiceUserExpirationBanner';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MicrosoftServiceUserQueryGql = gql`
  ${MicrosoftServiceUserExpirationBannerFragments.microsoftServiceUser}

  query MicrosoftServiceUserIntegrationExpiration {
    thisOrg {
      id
      integrations {
        id
        microsoftServiceUser {
          ...MicrosoftServiceUserExpirationBanner_microsoftServiceUser
        }
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MicrosoftCustomEmployeeQueryGql = gql`
  ${MicrosoftCustomExpirationBannerFragments.employee}

  query MicrosoftCustomEmployeeIntegrationExpiration($employeeIds: [uuid!]!) {
    employeeByIds(ids: $employeeIds) {
      ...MicrosoftCustomExpirationBanner_employee
    }
  }
`;

const MicrosoftIntegrationExpirationBannersInner: FC = () => {
  const [outlookSharedCalendarOwnerIds] = useOrgPrefStringArray(OrgPrefName.ARR_OUTLOOK_SHARED_CALENDAR_OWNER_IDS);

  // Always query for service user integration
  const { data: serviceUserData, loading: serviceUserLoading } = useMicrosoftServiceUserIntegrationExpirationQuery({
    fetchPolicy: 'network-only',
  });

  // Only query for employee integrations if we have employee IDs
  const hasEmployeeIds = (outlookSharedCalendarOwnerIds?.length ?? 0) > 0;
  const { data: employeeData, loading: employeeLoading } = useMicrosoftCustomEmployeeIntegrationExpirationQuery({
    variables: {
      employeeIds: outlookSharedCalendarOwnerIds || [],
    },
    skip: !hasEmployeeIds,
    fetchPolicy: 'network-only',
  });

  if (serviceUserLoading || employeeLoading) {
    return null;
  }

  const microsoftServiceUser = serviceUserData?.thisOrg?.integrations?.microsoftServiceUser;
  const employees = employeeData?.employeeByIds || [];

  return (
    <>
      <MicrosoftServiceUserExpirationBanner microsoftServiceUser={microsoftServiceUser} />
      <MicrosoftCustomExpirationBanner employees={employees} />
    </>
  );
};

const MicrosoftIntegrationExpirationBanners: FC = () => {
  const canManageOrgSettings = useHasUserPermission(Permission.CanManageOrgSettings);

  // Only show to users with admin (CanManageOrgSettings) permission
  if (!canManageOrgSettings) {
    return null;
  }

  return <MicrosoftIntegrationExpirationBannersInner />;
};

export default MicrosoftIntegrationExpirationBanners;
